// ═══════════════════════════════════════════════════════════════
// Settings — business profile + logo upload.
// One doc per mechanic at users/{uid}/profile/business. The values
// here get snapshot into shared estimate/invoice docs at Send time
// (see lib/use-business-profile.js → businessSnapshot()) so public
// pages can render branding without a second Firestore read.
//
// Logo upload: PNG keeps PNG (preserves transparency), JPG keeps JPG.
// Resized client-side to a 600px max dimension before upload.
// ═══════════════════════════════════════════════════════════════
import React, { useEffect, useState } from 'react';
import { Save, Upload, Trash2, Image as ImageIcon, Bell, BellOff, Smartphone, Plus, Wrench, Package, Hash, MessageSquarePlus, Percent } from 'lucide-react';
import { setDoc, serverTimestamp, getDocs, query, orderBy, writeBatch, doc } from 'firebase/firestore';
import { businessProfileDoc, jobsCol } from './database.js';
import { db } from './firebase.js';
import { allocateJobNumberBlock, FIRST_JOB_NUMBER } from './lib/job-number.js';
import ChecklistEditor from './ChecklistEditor.jsx';
import FeedbackModal from './FeedbackModal.jsx';
import DataExportSection from './DataExportSection.jsx';
import { useMyFeedback, feedbackStatusLabel } from './lib/use-feedback.js';
import {
  normalizeTaxPresets, blankTaxPreset, MAX_TAX_RATE,
} from './lib/tax-presets.js';
import { uploadFile, removeFile } from './lib/storage.js';
import { useBusinessProfile } from './lib/use-business-profile.js';
import { usePushNotifications } from './lib/use-push-notifications.js';
import { shortId, formatPrice } from './lib/job-helpers.js';
import {
  isDefaultLaborPreset,
  normalizeLaborPresets,
} from './lib/labor-presets.js';
import {
  DEFAULT_PART_SEARCH_LINKS,
  normalizePartSearchLinks,
  resolvePartSearchLinks,
} from './lib/part-search-links.js';

// Logos display at ~56px in the public branding header — anything over
// 400px is wasted bytes the customer's phone has to download. Dropped
// from 600 → 400 to make uploads ~3x faster without any visible loss.
// JPEG quality 0.82 (was 0.88) — same story, shaves another ~30%.
const MAX_LOGO_DIM = 400;
const LOGO_JPEG_QUALITY = 0.82;

// Resize the picked image to fit within MAX_LOGO_DIM, preserving format.
// PNG stays PNG so logos with transparent backgrounds keep their alpha.
async function compressLogo(file) {
  const isPng = file.type === 'image/png';
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_LOGO_DIM / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  canvas.getContext('2d').drawImage(bitmap, 0, 0, w, h);
  bitmap.close?.();
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Could not process image'))),
      isPng ? 'image/png' : 'image/jpeg',
      isPng ? undefined : LOGO_JPEG_QUALITY,
    );
  });
}

// Format a byte count as "12 KB" / "1.4 MB" — for the progress label
// so the user can see if the file is genuinely big or the network is
// slow.
function formatBytes(b) {
  if (b == null) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

export default function Settings() {
  const { profile, ready } = useBusinessProfile();

  const [name, setName]       = useState('');
  const [phone, setPhone]     = useState('');
  const [email, setEmail]     = useState('');
  const [address, setAddress] = useState('');
  const [logoUrl, setLogoUrl]   = useState('');
  const [logoPath, setLogoPath] = useState('');
  const [laborPresets, setLaborPresets] = useState([]);
  const [removedDefaultLaborPresetIds, setRemovedDefaultLaborPresetIds] = useState([]);
  const [taxPresets, setTaxPresets] = useState([]);
  const [partSearchLinks, setPartSearchLinks] = useState([]);

  const [saving, setSaving]           = useState(false);
  const [uploadStage, setUploadStage] = useState(null); // null | 'compressing' | 'uploading' | 'finalizing'
  const [uploadSize, setUploadSize]   = useState(null); // bytes after compression
  const [savedFlash, setSavedFlash]   = useState(false);
  const [error, setError]             = useState(null);
  const uploading = uploadStage !== null;

  // Hydrate the form once the profile snapshot arrives (or refreshes).
  // Plain primitive deps — the profile object identity changes per snapshot.
  useEffect(() => {
    if (!ready) return;
    setName(profile.name || '');
    setPhone(profile.phone || '');
    setEmail(profile.email || '');
    setAddress(profile.address || '');
    setLogoUrl(profile.logoUrl || '');
    setLogoPath(profile.logoPath || '');
    setLaborPresets(Array.isArray(profile.laborPresets) ? profile.laborPresets : []);
    setTaxPresets(Array.isArray(profile.taxPresets) ? profile.taxPresets : []);
    setRemovedDefaultLaborPresetIds(
      Array.isArray(profile.removedDefaultLaborPresetIds)
        ? profile.removedDefaultLaborPresetIds
        : []
    );
    setPartSearchLinks(
      resolvePartSearchLinks(profile.partSearchLinks).map((l) => ({ ...l }))
    );
  }, [
    ready,
    profile.name, profile.phone, profile.email, profile.address,
    profile.logoUrl, profile.logoPath, profile.laborPresets,
    profile.removedDefaultLaborPresetIds, profile.partSearchLinks, profile.taxPresets,
  ]);

  async function handleLogoChange(e) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file later
    if (!file) return;
    if (file.type !== 'image/png' && file.type !== 'image/jpeg') {
      setError('Please pick a PNG or JPG image.');
      return;
    }
    setError(null);
    try {
      // Compress on the canvas first — for a phone photo straight off
      // the camera roll this can take half a second.
      setUploadStage('compressing');
      setUploadSize(null);
      const ext = file.type === 'image/png' ? 'png' : 'jpg';
      const newPath = `business-logo.${ext}`;
      const blob = await compressLogo(file);
      setUploadSize(blob.size);

      // Plain uploadBytes — single POST, no CORS preflight. The
      // resumable variant requires the bucket to allow OPTIONS from
      // the web.app origin, which it doesn't out of the box. For
      // a 30-60 KB logo, single-shot finishes in 1-2 seconds anyway
      // so progress percentage isn't critical.
      setUploadStage('uploading');
      const url = await uploadFile(newPath, blob, { contentType: file.type });

      // Clean up the old extension if we just uploaded a different one.
      setUploadStage('finalizing');
      if (logoPath && logoPath !== newPath) {
        try { await removeFile(logoPath); } catch (_) { /* may not exist */ }
      }
      setLogoUrl(url);
      setLogoPath(newPath);
    } catch (e) {
      console.error('Logo upload failed:', e);
      setError(`Could not upload: ${e.message}`);
    } finally {
      setUploadStage(null);
      setUploadSize(null);
    }
  }

  async function handleRemoveLogo() {
    if (!confirm('Remove the business logo?')) return;
    setError(null);
    try {
      if (logoPath) {
        try { await removeFile(logoPath); } catch (_) { /* may already be gone */ }
      }
      setLogoUrl('');
      setLogoPath('');
    } catch (e) {
      setError(`Could not remove: ${e.message}`);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await setDoc(
        businessProfileDoc(),
        {
          name:    name.trim(),
          phone:   phone.trim(),
          email:   email.trim(),
          address: address.trim(),
          logoUrl: logoUrl || '',
          logoPath: logoPath || '',
          laborPresets: normalizeLaborPresets(laborPresets),
          removedDefaultLaborPresetIds,
          partSearchLinks: normalizePartSearchLinks(partSearchLinks),
          taxPresets: normalizeTaxPresets(taxPresets),
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2500);
    } catch (e) {
      console.error('Save settings failed:', e);
      setError(`Could not save: ${e.message}`);
    } finally {
      setSaving(false);
    }
  }

  if (!ready) {
    return (
      <div>
        <p className="muted small">Loading settings…</p>
      </div>
    );
  }

  return (
    <div>
      <div className="screen-header">
        <div>
          <h1>Settings</h1>
          <div className="subtitle">
            Business info shown on the estimates and invoices you send.
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="form settings-form">
        <section className="settings-section">
          <h2 className="settings-section-title">Business profile</h2>
          <p className="settings-section-help">
            This info appears at the top of the public estimate and invoice
            pages your customers see when they tap your link.
          </p>

          <label className="form-row">
            <span>Business name</span>
            <input
              type="text" maxLength={80}
              value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Driveway Auto Rx"
            />
          </label>

          <div className="form-row-pair">
            <label className="form-row">
              <span>Phone</span>
              <input
                type="tel" maxLength={20}
                value={phone} onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 123-4567"
              />
            </label>
            <label className="form-row">
              <span>Email</span>
              <input
                type="email" maxLength={120}
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
              />
            </label>
          </div>

          <label className="form-row">
            <span>Address</span>
            <textarea
              rows={2} maxLength={200}
              value={address} onChange={(e) => setAddress(e.target.value)}
              placeholder="Street, City, State"
            />
          </label>
        </section>

        <section className="settings-section">
          <h2 className="settings-section-title">Logo</h2>
          <p className="settings-section-help">
            PNG or JPG. We'll resize it to fit (max 400px) — keep the design
            simple so it prints clearly.
          </p>

          <div className="logo-row">
            <div className={`logo-preview ${!logoUrl ? 'empty' : ''}`}>
              {logoUrl
                ? <img src={logoUrl} alt="Business logo" />
                : <ImageIcon size={28} color="var(--text-3)" strokeWidth={1.5} />}
            </div>
            <div className="logo-actions">
              <label className="btn-secondary" style={{ cursor: uploading ? 'not-allowed' : 'pointer', opacity: uploading ? 0.6 : 1 }}>
                <Upload size={14} />
                {uploadStage === 'compressing' && 'Compressing…'}
                {uploadStage === 'uploading'   && `Uploading ${formatBytes(uploadSize)}…`}
                {uploadStage === 'finalizing'  && 'Finalizing…'}
                {!uploading && (logoUrl ? 'Replace' : 'Upload logo')}
                <input
                  type="file" accept="image/png,image/jpeg"
                  onChange={handleLogoChange}
                  disabled={uploading}
                  style={{ display: 'none' }}
                />
              </label>
              {logoUrl && !uploading && (
                <button
                  type="button"
                  className="btn-link danger"
                  onClick={handleRemoveLogo}
                  disabled={uploading}
                >
                  <Trash2 size={14} /> Remove
                </button>
              )}
            </div>
            {uploading && (
              <div className="logo-progress-bar" aria-hidden>
                <div className="logo-progress-fill logo-progress-indeterminate" />
              </div>
            )}
          </div>
        </section>

        <section className="settings-section">
          <h2 className="settings-section-title">
            <Wrench size={16} style={{ verticalAlign: '-2px', marginRight: 6 }} />
            Labor presets
          </h2>
          <p className="settings-section-help">
            Saved flat-rate jobs — tap a preset when adding labor to a job.
          </p>
          <ul className="labor-preset-list">
            {laborPresets.map((p, idx) => (
              <li key={p.id || idx} className="labor-preset-row">
                <input
                  type="text"
                  className="labor-preset-desc"
                  placeholder="Oil change"
                  value={p.description || ''}
                  onChange={(e) => {
                    const next = [...laborPresets];
                    next[idx] = { ...next[idx], description: e.target.value };
                    setLaborPresets(next);
                  }}
                />
                <div className="labor-preset-amount">
                  <span>$</span>
                  <input
                    type="number" min={0} step="0.01" inputMode="decimal"
                    value={p.amount === 0 ? '' : p.amount}
                    onChange={(e) => {
                      const next = [...laborPresets];
                      next[idx] = { ...next[idx], amount: Number(e.target.value) || 0 };
                      setLaborPresets(next);
                    }}
                  />
                </div>
                <button
                  type="button"
                  className="btn-link danger"
                  onClick={() => {
                    if (isDefaultLaborPreset(p)) {
                      setRemovedDefaultLaborPresetIds((ids) =>
                        ids.includes(p.id) ? ids : [...ids, p.id]
                      );
                    }
                    setLaborPresets(laborPresets.filter((_, i) => i !== idx));
                  }}
                  aria-label="Remove preset"
                >
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
          <button
            type="button"
            className="btn-link"
            onClick={() => setLaborPresets([...laborPresets, { id: shortId(), description: '', amount: 0 }])}
          >
            <Plus size={14} /> Add preset
          </button>
        </section>

        <section className="settings-section">
          <h2 className="settings-section-title">
            <Percent size={16} style={{ verticalAlign: '-2px', marginRight: 6 }} />
            Tax rates
          </h2>
          <p className="settings-section-help">
            Saved tax rates, named however you think of them — a county, a
            town, a customer's yard. Pick one when you add tax to a job.
            Parts and labor are separate because many states tax one and
            not the other; leave a rate at 0 if it isn't taxed.
          </p>
          <ul className="tax-preset-list">
            {taxPresets.map((p, idx) => {
              const patch = (fields) => {
                const next = [...taxPresets];
                next[idx] = { ...next[idx], ...fields };
                setTaxPresets(next);
              };
              return (
                <li key={p.id || idx} className="tax-preset-row">
                  <input
                    type="text"
                    maxLength={60}
                    className="tax-preset-label"
                    placeholder="Lancaster County"
                    value={p.label || ''}
                    onChange={(e) => patch({ label: e.target.value })}
                  />
                  <div className="tax-preset-rates">
                    <label className="tax-preset-rate">
                      <span className="muted small">Parts</span>
                      <div className="tax-preset-rate-input">
                        <input
                          type="number" min={0} max={MAX_TAX_RATE} step="0.001"
                          inputMode="decimal"
                          value={p.partsRate === 0 ? '' : (p.partsRate ?? '')}
                          placeholder="0"
                          onChange={(e) => patch({ partsRate: e.target.value })}
                        />
                        <span>%</span>
                      </div>
                    </label>
                    <label className="tax-preset-rate">
                      <span className="muted small">Labor</span>
                      <div className="tax-preset-rate-input">
                        <input
                          type="number" min={0} max={MAX_TAX_RATE} step="0.001"
                          inputMode="decimal"
                          value={p.laborRate === 0 ? '' : (p.laborRate ?? '')}
                          placeholder="0"
                          onChange={(e) => patch({ laborRate: e.target.value })}
                        />
                        <span>%</span>
                      </div>
                    </label>
                  </div>
                  <label className="tax-preset-supplies">
                    <input
                      type="checkbox"
                      checked={p.taxSupplies !== false}
                      onChange={(e) => patch({ taxSupplies: e.target.checked })}
                    />
                    <span className="muted small">Tax shop supplies</span>
                  </label>
                  <button
                    type="button"
                    className="btn-link danger tax-preset-remove"
                    onClick={() => setTaxPresets(taxPresets.filter((_, i) => i !== idx))}
                    aria-label="Remove tax rate"
                  >
                    <Trash2 size={14} />
                  </button>
                </li>
              );
            })}
          </ul>
          {taxPresets.length === 0 && (
            <p className="muted small">
              None saved. You can also save one straight from a job the first
              time you type it in.
            </p>
          )}
          <button
            type="button"
            className="btn-link"
            onClick={() => setTaxPresets([...taxPresets, blankTaxPreset(shortId())])}
          >
            <Plus size={14} /> Add a tax rate
          </button>
        </section>

        <section className="settings-section">
          <h2 className="settings-section-title">
            <Package size={16} style={{ verticalAlign: '-2px', marginRight: 6 }} />
            Part search links
          </h2>
          <p className="settings-section-help">
            Catalog sites opened when you look up a part on a job. Use{' '}
            <code className="inline-code">{'{q}'}</code> where the search term goes.
          </p>
          <ul className="part-search-link-list">
            {partSearchLinks.map((link, idx) => (
              <li key={link.id || idx} className="part-search-link-row">
                <input
                  type="text"
                  className="part-search-link-label"
                  placeholder="NAPA"
                  value={link.label || ''}
                  onChange={(e) => {
                    const next = [...partSearchLinks];
                    next[idx] = { ...next[idx], label: e.target.value };
                    setPartSearchLinks(next);
                  }}
                />
                <input
                  type="url"
                  className="part-search-link-url"
                  placeholder="https://example.com/search?q={q}"
                  value={link.url || ''}
                  onChange={(e) => {
                    const next = [...partSearchLinks];
                    next[idx] = { ...next[idx], url: e.target.value };
                    setPartSearchLinks(next);
                  }}
                />
                <button
                  type="button"
                  className="btn-link danger"
                  onClick={() => setPartSearchLinks(partSearchLinks.filter((_, i) => i !== idx))}
                  aria-label="Remove link"
                >
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
          <div className="part-search-link-actions">
            <button
              type="button"
              className="btn-link"
              onClick={() => setPartSearchLinks([
                ...partSearchLinks,
                { id: shortId(), label: '', url: 'https://' },
              ])}
            >
              <Plus size={14} /> Add link
            </button>
            <button
              type="button"
              className="btn-link"
              onClick={() => setPartSearchLinks(DEFAULT_PART_SEARCH_LINKS.map((l) => ({ ...l })))}
            >
              Restore defaults
            </button>
          </div>
        </section>

        {error && <p className="form-error">{error}</p>}

        <div className="form-actions" style={{ alignItems: 'center' }}>
          {savedFlash && !saving && (
            <span className="muted small" style={{ marginRight: 'auto', color: 'var(--status-approved-fg)' }}>
              ✓ Saved
            </span>
          )}
          <button type="submit" className="btn" disabled={saving || uploading}>
            <Save size={16} strokeWidth={2.5} />
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>

      <DataExportSection />
      <FeedbackSection />
      <ChecklistEditor />
      <NotificationsSection />
      <JobNumbersSection />
    </div>
  );
}

// ─── Feedback ────────────────────────────────────────────────────
function FeedbackSection() {
  const { items, ready } = useMyFeedback();
  const [open, setOpen] = useState(false);

  return (
    <section className="settings-section settings-section-standalone">
      <div className="settings-section-head">
        <MessageSquarePlus size={16} />
        <div>
          <h2>Feedback</h2>
          <p className="muted small">
            Tell us what's missing and what's broken, or request an improvement.
          </p>
        </div>
      </div>

      <div className="form-actions" style={{ marginBottom: 14 }}>
        <button type="button" className="btn" onClick={() => setOpen(true)}>
          <MessageSquarePlus size={16} strokeWidth={2.5} /> Send feedback
        </button>
      </div>

      {ready && items.length > 0 && (
        <>
          <p className="muted small" style={{ marginBottom: 8 }}>
            What you've sent so far:
          </p>
          <ul className="fb-mine">
            {items.map((f) => (
              <li key={f.id} className="fb-mine-row">
                <span className="fb-mine-title">{f.title}</span>
                <span className={`fb-mine-status fb-${f.status || 'new'}`}>
                  {feedbackStatusLabel(f.status)}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      {open && <FeedbackModal onClose={() => setOpen(false)} />}
    </section>
  );
}

// ─── Job numbers — backfill for pre-numbering tickets ──────────────
// Jobs created from here on get a number automatically. Jobs that
// already existed when numbering shipped have none, so they'd show a
// blank where every other ticket shows EST-#### — which reads as a
// bug rather than as history. This assigns them numbers in the order
// they were created (oldest gets the lowest number), so the sequence
// still tells the truth about what came first.
//
// Deliberately a button, not an automatic migration: it's a one-time
// write across every existing job and the mechanic should choose when
// that happens.
function JobNumbersSection() {
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  async function handleBackfill() {
    setScanning(true);
    setError(null);
    setResult(null);
    try {
      const snap = await getDocs(query(jobsCol(), orderBy('createdAt', 'asc')));
      const unnumbered = snap.docs.filter(
        (d) => !Number.isFinite(Number(d.data()?.jobNumber))
      );
      if (unnumbered.length === 0) {
        setResult('Every job already has a number — nothing to do.');
        return;
      }
      // Reserve the whole run up front so a half-finished backfill
      // can't hand the same digits to a job created mid-run.
      const start = await allocateJobNumberBlock(unnumbered.length);
      if (start == null) throw new Error('Could not reserve numbers.');

      // Firestore caps a batch at 500 writes.
      const CHUNK = 400;
      for (let i = 0; i < unnumbered.length; i += CHUNK) {
        const batch = writeBatch(db);
        unnumbered.slice(i, i + CHUNK).forEach((d, k) => {
          batch.update(doc(jobsCol(), d.id), { jobNumber: start + i + k });
        });
        await batch.commit();
      }
      const last = start + unnumbered.length - 1;
      setResult(
        `Numbered ${unnumbered.length} older ${unnumbered.length === 1 ? 'job' : 'jobs'} ` +
        `(${start}–${last}).`
      );
    } catch (e) {
      console.error('Job number backfill failed:', e);
      setError(e.message);
    } finally {
      setScanning(false);
    }
  }

  return (
    <section className="settings-section settings-section-standalone">
      <div className="settings-section-head">
        <Hash size={16} />
        <div>
          <h2>Job numbers</h2>
          <p className="muted small">
            Every new job gets its own number, starting at {FIRST_JOB_NUMBER}. The estimate
            and the invoice for a job share the same digits — EST-1042 becomes INV-1042 — so
            a customer can read one back to you and you'll find the ticket either way.
          </p>
        </div>
      </div>

      <p className="muted small" style={{ marginBottom: 12 }}>
        Jobs you created before numbering existed don't have one yet. This gives them
        numbers in the order they were created. Safe to run more than once — it only
        touches jobs that are still missing a number.
      </p>

      {error && <p className="form-error" style={{ marginBottom: 8 }}>{error}</p>}
      {result && (
        <p className="muted small" style={{ marginBottom: 8, color: 'var(--status-approved-fg)' }}>
          ✓ {result}
        </p>
      )}

      <div className="form-actions">
        <button type="button" className="btn-secondary" onClick={handleBackfill} disabled={scanning}>
          <Hash size={16} strokeWidth={2.5} />
          {scanning ? 'Numbering…' : 'Number older jobs'}
        </button>
      </div>
    </section>
  );
}

// ─── Notifications — push enable/disable per device ────────────────
// One toggle per device (phone, tablet, desktop browser). The first
// time you tap Enable, the browser asks for permission. After granting,
// an FCM token is stored on users/{uid}/devices/{deviceId} so the
// Cloud Function knows where to push when a customer approves an
// estimate. Disable removes this device's token; other devices stay
// enabled. Browser-level block requires re-allowing in OS settings.
function NotificationsSection() {
  const { permission, busy, error, supported, enable, disable, token } = usePushNotifications();

  const enabled = permission === 'granted' && !!token;
  const blocked = permission === 'denied';

  return (
    <section className="settings-section settings-section-standalone">
      <div className="settings-section-head">
        <Bell size={16} />
        <div>
          <h2>Notifications</h2>
          <p className="muted small">
            Buzz this device when a customer approves an estimate. Enable per device.
          </p>
        </div>
      </div>

      <div className="notif-status">
        {!supported && (
          <p className="notif-state notif-state-unsupported">
            <BellOff size={14} />
            This browser doesn't support push notifications.
            {' '}
            <span className="muted small">
              On iPhone, install Bayless to your home screen first (Share → Add to Home Screen),
              then come back here.
            </span>
          </p>
        )}

        {supported && enabled && (
          <p className="notif-state notif-state-enabled">
            <Smartphone size={14} />
            Enabled on this device.
            <span className="muted small">
              {' '}You'll get a notification when a customer approves an estimate.
            </span>
          </p>
        )}

        {supported && !enabled && !blocked && (
          <p className="notif-state notif-state-default">
            <BellOff size={14} />
            Off on this device. Tap Enable to turn on.
          </p>
        )}

        {supported && blocked && (
          <p className="notif-state notif-state-blocked">
            <BellOff size={14} />
            Notifications are blocked by your browser/OS for this site.
            {' '}
            <span className="muted small">
              Re-allow them in your browser settings (or iOS Settings → Notifications → Bayless)
              and refresh.
            </span>
          </p>
        )}

        {error && <p className="form-error" style={{ marginTop: 8 }}>{error.message}</p>}

        <div className="form-actions" style={{ marginTop: 12 }}>
          {supported && !enabled && !blocked && (
            <button type="button" className="btn" onClick={enable} disabled={busy}>
              <Bell size={16} strokeWidth={2.5} />
              {busy ? 'Enabling…' : 'Enable notifications'}
            </button>
          )}
          {supported && enabled && (
            <button type="button" className="btn-secondary" onClick={disable} disabled={busy}>
              <BellOff size={16} strokeWidth={2.5} />
              {busy ? 'Disabling…' : 'Disable on this device'}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
